import axios from 'axios';
import type { AuthTokenService } from './AuthTokenService';

export interface RepScoreResponse {
  currentScore: number;
  maxScore: number;
  scoreLevel: string;
  pssEligible: boolean;
  earlyPickupStatus?: {
    eligibleForFuturePlans: boolean;
    rejectionReasons: string[];
    completedPlans: number;
    requiredCompletedPlans: number;
    currentRepScore: number;
    minRepScoreRequired: number;
    hasMeaningfulPaymentHistory: boolean;
    hasActiveEarlyPickupPlan: boolean;
  };
  maxPssAmount: string;
  pssCreditPercentage: string;
  scoreBreakdown: {
    paymentHistory: {
      score: number;
      maxScore: number;
      onTimePayments: number;
      latePayments: number;
      missedPayments: number;
      totalPayments: number;
      successRate: number;
    };
    profileCompletion: {
      score: number;
      maxScore: number;
      completionPercentage: number;
      missingVerifications: string[];
    };
    accountAge: {
      score: number;
      maxScore: number;
      daysActive: number;
      accountCreated: string;
    };
    purchaseBehavior: {
      score: number;
      maxScore: number;
      totalOrders: number;
      avgOrderValue: string;
      lastPurchase: string;
    };
  };
  scoreHistory: Array<{
    date: string;
    score: number;
    change: number;
    trigger: string;
  }>;
  nextLevelThreshold: number;
  lastUpdated: string;
}

export class RepScoreService {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.authTokenService.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await this.authTokenService.refreshToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return this.apiClient(originalRequest);
        }
        return Promise.reject(error);
      }
    );
  }

  async getUserRepScore(_userId: string): Promise<RepScoreResponse> {
    const response = await this.apiClient.get('/v1/users/me/repscore');
    const data = (response.data ?? {}) as any;

    // Backend canonical shape (Django RepScoreSerializer)
    // {
    //   score: { current, max, tier, last_calculated },
    //   components: { payment_history, credit_utilization, account_age },
    //   statistics: { total_payments, on_time_payments, late_payments, missed_payments, payment_success_rate },
    //   credit_summary: { early_pickup_percentage, eligible, next_tier_score },
    //   history: []
    // }
    if (data && typeof data === 'object' && data.score) {
      const score = (data.score ?? {}) as any;
      const components = (data.components ?? {}) as any;
      const statistics = (data.statistics ?? {}) as any;
      const creditSummary = (data.credit_summary ?? {}) as any;
      const earlyPickupStatus = (creditSummary.early_pickup_status ?? {}) as any;

      const paymentHistoryComponent = (components.payment_history ?? {}) as any;
      const creditUtilizationComponent = (components.credit_utilization ?? {}) as any;
      const accountAgeComponent = (components.account_age ?? {}) as any;

      const history = Array.isArray(data.history) ? data.history : [];

      return {
        currentScore: Number(score.current ?? 0),
        maxScore: Number(score.max ?? 1000),
        scoreLevel: String(score.tier ?? 'unknown'),
        pssEligible: Boolean(creditSummary.eligible),
        earlyPickupStatus: creditSummary.early_pickup_status
          ? {
              eligibleForFuturePlans: Boolean(
                earlyPickupStatus.eligible_for_future_plans
              ),
              rejectionReasons: Array.isArray(
                earlyPickupStatus.rejection_reasons
              )
                ? earlyPickupStatus.rejection_reasons
                : [],
              completedPlans: Number(earlyPickupStatus.completed_plans ?? 0),
              requiredCompletedPlans: Number(
                earlyPickupStatus.required_completed_plans ?? 0
              ),
              currentRepScore: Number(
                earlyPickupStatus.current_rep_score ?? score.current ?? 0
              ),
              minRepScoreRequired: Number(
                earlyPickupStatus.min_rep_score_required ?? 0
              ),
              hasMeaningfulPaymentHistory: Boolean(
                earlyPickupStatus.has_meaningful_payment_history
              ),
              hasActiveEarlyPickupPlan: Boolean(
                earlyPickupStatus.has_active_early_pickup_plan
              ),
            }
          : undefined,
        // Backend doesn't currently provide an absolute max PSS amount; keep as 0 until we add it.
        maxPssAmount: '0',
        pssCreditPercentage: String(creditSummary.early_pickup_percentage ?? '0'),
        scoreBreakdown: {
          paymentHistory: {
            score: Number(paymentHistoryComponent.score ?? 0),
            maxScore: 0,
            onTimePayments: Number(statistics.on_time_payments ?? 0),
            latePayments: Number(statistics.late_payments ?? 0),
            missedPayments: Number(statistics.missed_payments ?? 0),
            totalPayments: Number(statistics.total_payments ?? 0),
            successRate: Number(statistics.payment_success_rate ?? 0),
          },
          // Not part of backend contract yet; keep placeholders for current UI components.
          profileCompletion: {
            score: 0,
            maxScore: 0,
            completionPercentage: 0,
            missingVerifications: [],
          },
          accountAge: {
            score: Number(accountAgeComponent.score ?? 0),
            maxScore: 0,
            daysActive: 0,
            accountCreated: '',
          },
          // Surface credit utilization under purchase behavior until UI is refactored to use backend components directly.
          purchaseBehavior: {
            score: Number(creditUtilizationComponent.score ?? 0),
            maxScore: 0,
            totalOrders: 0,
            avgOrderValue: '0',
            lastPurchase: '',
          },
        },
        scoreHistory: history.map((item: any) => ({
          date: String(item?.date ?? ''),
          score: Number(item?.score ?? 0),
          change: Number(item?.change ?? 0),
          trigger: String(item?.trigger ?? ''),
        })),
        nextLevelThreshold: Number(creditSummary.next_tier_score ?? 0),
        lastUpdated: String(score.last_calculated ?? ''),
      };
    }

    const breakdown = (data.score_breakdown ?? {}) as any;
    const paymentHistory = (breakdown.payment_history ?? {}) as any;
    const profileCompletion = (breakdown.profile_completion ?? {}) as any;
    const accountAge = (breakdown.account_age ?? {}) as any;
    const purchaseBehavior = (breakdown.purchase_behavior ?? {}) as any;

    const scoreHistory = Array.isArray(data.score_history) ? data.score_history : [];

    return {
      currentScore: Number(data.current_score ?? 0),
      maxScore: Number(data.max_score ?? 0),
      scoreLevel: String(data.score_level ?? 'unknown'),
      pssEligible: Boolean(data.pss_eligible),
      earlyPickupStatus: undefined,
      maxPssAmount: String(data.max_pss_amount ?? '0'),
      pssCreditPercentage: String(data.pss_credit_percentage ?? '0'),
      scoreBreakdown: {
        paymentHistory: {
          score: Number(paymentHistory.score ?? 0),
          maxScore: Number(paymentHistory.max_score ?? 0),
          onTimePayments: Number(paymentHistory.on_time_payments ?? 0),
          latePayments: Number(paymentHistory.late_payments ?? 0),
          missedPayments: Number(paymentHistory.missed_payments ?? 0),
          totalPayments: Number(paymentHistory.total_payments ?? 0),
          successRate: Number(paymentHistory.success_rate ?? 0),
        },
        profileCompletion: {
          score: Number(profileCompletion.score ?? 0),
          maxScore: Number(profileCompletion.max_score ?? 0),
          completionPercentage: Number(profileCompletion.completion_percentage ?? 0),
          missingVerifications: Array.isArray(profileCompletion.missing_verifications)
            ? profileCompletion.missing_verifications
            : [],
        },
        accountAge: {
          score: Number(accountAge.score ?? 0),
          maxScore: Number(accountAge.max_score ?? 0),
          daysActive: Number(accountAge.days_active ?? 0),
          accountCreated: String(accountAge.account_created ?? ''),
        },
        purchaseBehavior: {
          score: Number(purchaseBehavior.score ?? 0),
          maxScore: Number(purchaseBehavior.max_score ?? 0),
          totalOrders: Number(purchaseBehavior.total_orders ?? 0),
          avgOrderValue: String(purchaseBehavior.avg_order_value ?? '0'),
          lastPurchase: String(purchaseBehavior.last_purchase ?? ''),
        },
      },
      scoreHistory: scoreHistory.map((item: any) => ({
        date: String(item?.date ?? ''),
        score: Number(item?.score ?? 0),
        change: Number(item?.change ?? 0),
        trigger: String(item?.trigger ?? ''),
      })),
      nextLevelThreshold: Number(data.next_level_threshold ?? 0),
      lastUpdated: String(data.last_updated ?? ''),
    };
  }
}

// Repository interface implementation
export interface IRepScoreRepository {
  getUserRepScore(userId: string): Promise<RepScoreResponse>;
}

export class ApiRepScoreRepository implements IRepScoreRepository {
  constructor(private repScoreService: RepScoreService) {}

  async getUserRepScore(userId: string): Promise<RepScoreResponse> {
    return this.repScoreService.getUserRepScore(userId);
  }
}
